import { cn } from "@/lib/class-control";
import { useLocation } from "wouter";
import { Link } from "wouter";
import {
  BarChart3,
  FolderOpen,
  DollarSign,
  FileText,
  BarChart,
  FileCheck,
  Settings,
  ChevronDown,
  ChevronRight,
  Users,
  Play,
} from "lucide-react";
import { useState } from "react";

interface SidebarProps {
  isCollapsed?: boolean;
}

interface SidebarItemProps {
  icon: React.ReactNode;
  label: string;
  href?: string;
  isActive: boolean;
  hasSubmenu?: boolean;
  isExpanded?: boolean;
  onToggle?: () => void;
  iconColor?: string;
  children?: React.ReactNode;
  isCollapsed?: boolean;
}

interface SidebarSectionProps {
  title: string;
  children: React.ReactNode;
  isCollapsed?: boolean;
}

export default function Sidebar({ isCollapsed = false }: SidebarProps) {
  const [location] = useLocation();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    financeiro: false,
    projetos: false,
    contratual: false,
    executivo: false,
    configuracoes: false,
  });

  const toggleSection = (section: string) => {
    if (isCollapsed) return; // Não permite expandir quando colapsado
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <aside className={cn(
      "bg-darkblue rounded-lg text-white transition-all duration-300",
      "overflow-y-auto flex flex-col",
      isCollapsed ? "w-16" : "w-45"
    )}>
      {/* Header with Logo and User */}
      <div className="h-12 flex items-center justify-between px-3">
        <div className={cn(
          "flex items-center gap-1.5 transition-all duration-300",
          isCollapsed && "justify-center w-full"
        )}>
          <div className="w-5 h-5 bg-teal-500 rounded flex items-center justify-center flex-shrink-0">
            <span className="text-white font-bold text-[10px]">CB</span>
          </div>
          {!isCollapsed && (
            <span className="text-base font-semibold text-white whitespace-nowrap">
              cineblocks
            </span>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="py-2.5 flex-1">
        <div className="space-y-4">
          {/* LINKS RÁPIDOS */}
          <SidebarSection title="LINKS RÁPIDOS" isCollapsed={isCollapsed}>
            <SidebarItem
              icon={<BarChart3 className="w-4 h-4" />}
              label="Dashboard"
              href="/dashboard"
              isActive={location === "/dashboard"}
              iconColor="text-green-400"
              isCollapsed={isCollapsed}
            />
          </SidebarSection>

          {/* PROJETOS */}
          <SidebarSection title="PROJETOS" isCollapsed={isCollapsed}>
            <SidebarItem
              icon={<FolderOpen className="w-4 h-4" />}
              label="Projetos"
              isActive={location.startsWith("/projetos")}
              hasSubmenu={true}
              isExpanded={expandedSections.projetos}
              onToggle={() => toggleSection('projetos')}
              iconColor="text-yellow-400"
              isCollapsed={isCollapsed}
            >
              <div className={cn(
                "ml-5 mt-0.5 space-y-0.5 overflow-hidden transition-all duration-300 ease-in-out",
                expandedSections.projetos ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
              )}>
                <SubMenuItem
                  label="Ver Todos"
                  href="/projetos"
                  isActive={location === "/projetos"}
                />
                <SubMenuItem
                  label="Novo Projeto"
                  href="/cadastrar-projeto"
                  isActive={location === "/cadastrar-projeto"}
                  isLast={true}
                />
              </div>
            </SidebarItem>
          </SidebarSection>

          {/* FINANCEIRO */}
          <SidebarSection title="FINANCEIRO" isCollapsed={isCollapsed}>
            <SidebarItem
              icon={<DollarSign className="w-4 h-4" />}
              label="Financeiro"
              isActive={location.startsWith("/financeiro")}
              hasSubmenu={true}
              isExpanded={expandedSections.financeiro}
              onToggle={() => toggleSection('financeiro')}
              iconColor="text-green-400"
              isCollapsed={isCollapsed}
            >
              <div className={cn(
                "ml-5 mt-0.5 space-y-0.5 overflow-hidden transition-all duration-300 ease-in-out",
                expandedSections.financeiro ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
              )}>
                <SubMenuItem
                  label="Gestão de Recursos"
                  href="/financeiro/gestao-recursos"
                  isActive={location === "/financeiro/gestao-recursos"}
                />
                <SubMenuItem
                  label="Demonstrativo Extrato Ba..."
                  href="/financeiro/demonstrativo"
                  isActive={location === "/financeiro/demonstrativo"}
                />
                <SubMenuItem
                  label="Orçamentos"
                  href="/financeiro/orcamentos"
                  isActive={location === "/financeiro/orcamentos"}
                />
                <SubMenuItem
                  label="Relação de Pagamentos"
                  href="/financeiro/relacao-pagamentos"
                  isLast={true}
                  isActive={location === "/financeiro/relacao-pagamentos"}
                />
              </div>
            </SidebarItem>
          </SidebarSection>

          {/* CONTRATUAL */}
          <SidebarSection title="CONTRATUAL" isCollapsed={isCollapsed}>
            <SidebarItem
              icon={<Users className="w-4 h-4" />}
              label="Contratual"
              isActive={location.startsWith("/contratual")}
              hasSubmenu={true}
              isExpanded={expandedSections.contratual}
              onToggle={() => toggleSection('contratual')}
              iconColor="text-purple-400"
              isCollapsed={isCollapsed}
            >
              <div className={cn(
                "ml-5 mt-0.5 space-y-0.5 overflow-hidden transition-all duration-300 ease-in-out",
                expandedSections.contratual ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
              )}>
                <SubMenuItem
                  label="Contratos"
                  href="/contratual/contratos"
                  isActive={location === "/contratual/contratos"}
                />
                <SubMenuItem
                  label="Gestão de Equipe"
                  href="/contratual/gestao-equipe"
                  isActive={location === "/contratual/gestao-equipe"}
                />
                <SubMenuItem
                  label="Gestão de Tarefas"
                  href="/documentacao/gestao-tarefas"
                  isActive={location === "/documentacao/gestao-tarefas"}
                />
              </div>
            </SidebarItem>
          </SidebarSection>

          {/* EXECUTIVO */}
          <SidebarSection title="EXECUTIVO" isCollapsed={isCollapsed}>
            <SidebarItem
              icon={<BarChart className="w-4 h-4" />}
              label="Relatórios"
              isActive={location.startsWith("/relatorios")}
              hasSubmenu={true}
              isExpanded={expandedSections.executivo}
              onToggle={() => toggleSection('executivo')}
              iconColor="text-blue-400"
              isCollapsed={isCollapsed}
            >
              <div className={cn(
                "ml-5 mt-0.5 space-y-0.5 overflow-hidden transition-all duration-300 ease-in-out",
                expandedSections.executivo ? "max-h-48 opacity-100" : "max-h-0 opacity-0"
              )}>
                <SubMenuItem
                  label="Relatórios Financeiros"
                  href="/relatorios/financeiros"
                  isActive={location === "/relatorios/financeiros"}
                />
              </div>
            </SidebarItem>
            <SidebarItem
              icon={<FileCheck className="w-4 h-4" />}
              label="Documentação"
              href="/documentacao"
              isActive={location.startsWith("/documentacao")}
              iconColor="text-orange-400"
              isCollapsed={isCollapsed}
            />
          </SidebarSection>

          {/* OUTROS */}
          <SidebarSection title="OUTROS" isCollapsed={isCollapsed}>
            <SidebarItem
              icon={<Settings className="w-4 h-4" />}
              label="Configurações"
              href="/configuracoes"
              isActive={location.startsWith("/configuracoes")}
              iconColor="text-teal-400"
              isCollapsed={isCollapsed}
            />
          </SidebarSection>
        </div>
      </nav>
    </aside>
  );
}

function SidebarSection({ title, children, isCollapsed }: SidebarSectionProps) {
  return (
    <div className={cn("mb-3", isCollapsed ? "px-0" : "px-3")}>
      {!isCollapsed && (
        <h3 className="text-[.55rem] font-medium text-gray-400 uppercase tracking-wider mb-1.5 px-2">
          {title}
        </h3>
      )}
      <div className="space-y-0.5">
        {children}
      </div>
    </div>
  );
}

function SidebarItem({
  icon,
  label,
  href,
  isActive,
  hasSubmenu = false,
  isExpanded = false,
  onToggle,
  iconColor = "text-gray-400",
  children,
  isCollapsed = false
}: SidebarItemProps) {
  const content = (
    <div className={cn(
      "flex items-center justify-between py-1.5 text-sm rounded-md transition-all duration-200 cursor-pointer group",
      isCollapsed ? "px-0 mx-auto w-10 justify-center" : "px-2.5",
      isActive
        ? "bg-[#2a2f4a] text-white border-l border-teal-400"
        : "text-gray-300 hover:bg-[#252841] hover:text-white"
    )}>
      <div className={cn(
        "flex items-center",
        isCollapsed ? "justify-center" : "gap-2"
      )}>
        <span className={iconColor}>{icon}</span>
        {!isCollapsed && <span className="font-medium">{label}</span>}
      </div>
      {hasSubmenu && !isCollapsed && (
        <span className="text-gray-400 group-hover:text-white transition-colors">
          {isExpanded ? (
            <ChevronDown className="w-3.5 h-3.5" />
          ) : (
            <ChevronRight className="w-3.5 h-3.5" />
          )}
        </span>
      )}
    </div>
  );

  return (
    <div>
      {href && !hasSubmenu ? (
        <Link href={href}>
          {content}
        </Link>
      ) : (
        <div onClick={onToggle}>
          {content}
        </div>
      )}
      {!isCollapsed && children}
    </div>
  );
}

function SubMenuItem({ label, href, isActive, isLast }: { label: string; href: string; isActive: boolean; isLast?: boolean }) {
  return (
    <Link href={href} className={cn(
      "block px-2 py-1 text-[11px] rounded-md transition-colors duration-200",
      isActive
        ? "bg-[#2a2f4a] text-white"
        : "text-gray-400 hover:bg-[#252841] hover:text-white",
      isLast && "mb-1"
    )}>
      <div className="flex items-center gap-1.5">
        <Play size={7} className="text-primary" />
        {label}
      </div>
    </Link>
  );
}
